import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { faBan, faClose } from "@fortawesome/free-solid-svg-icons"
import ModalPortal from "../../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../../components/Animate/Animate"
import defaultStyle from "../../../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../../../../components/Titles/Titles"
import { ButtonIcon } from "../../../../components/Button/Button"
import InputField from "../../../../components/InputField/InputField"
import { PopupHelpButton } from "../../../../components/Help/PopupHelpButton"
import THEME from "../../../../configuration/Theme"
import { SIZE } from "../../../../configuration/Sizes"

type Props = {
  isOpen: boolean
  onClose: () => void
  onConfirm: (reason: string) => void
}

export const FiscalCancelPopup: React.FC<Props> = ({ isOpen, onClose, onConfirm }) => {
  const { t } = useTranslation(["fiscalIntegration", "helpPopup"])

  const [reason, setReason] = useState("")

  if (!isOpen) {
    return null
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              icon={faBan}
              iconColor={THEME.colors.buttonBGCloseColor}
              title={t("documents.cancelTitle")}
            >
              <div className={`${defaultStyle.ButtonsActions} gap-1`}>
                <PopupHelpButton
                  variant="global"
                  helpKey="pages.myrestaurant.fiscalintegration.cancel.1"
                  title={t("helpPopup:global.fiscalCancel.title")}
                  description={t("helpPopup:global.fiscalCancel.description")}
                  steps={t("helpPopup:global.fiscalCancel.steps", { returnObjects: true }) as string[]}
                  bullets={t("helpPopup:global.fiscalCancel.bullets", { returnObjects: true }) as string[]}
                />

                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalCancelPopup.button.onClose"
                />
              </div>
            </TitlePageResponsiveIconSmall>

            <div className="space-y-3 p-2">
              <p className="text-sm leading-relaxed text-slate-600">{t("documents.cancelDescription")}</p>

              <InputField
                type="text"
                value={reason}
                minLength={15}
                maxLength={255}
                placeholder={t("documents.cancelReason")}
                onChange={(event) => setReason(event.target.value)}
                descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalCancelPopup.input.cancelReason"
              />

              <button
                type="button"
                disabled={reason.trim().length < 15}
                onClick={() => onConfirm(reason.trim())}
                className="w-full rounded-xl bg-red-600 px-4 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalCancelPopup.button.onConfirmReasonTrim"
              >
                {t("documents.confirmCancel")}
              </button>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
